import { useRef } from "react";
import { v4 } from "uuid";
import { INPUT_MAX_LENGTH } from "@/components/AIComponents/ExportablesModal";
import ExportablesTemplateHeaderSection from "@/components/AIComponents/ExportablesModal/templates/components/ExportablesTemplateHeaderSection";
import { TemplateTextArea } from "@/components/AIComponents/ExportablesModal/templates/components/TemplateTextArea";
import { type TemplateProps } from "@/components/AIComponents/ExportablesModal/templates/registry";
import { type CauseAndEffectFormData } from "@/types/models/Exportables.interface";
import ThinArrowRight from "@/icons/ThinArrowRight/ThinArrowRight";
import {
  arrowSvgCss,
  columnHeaderCss,
  containerCss,
  headersRowCss,
  pairsContainerCss,
  pairRowCss,
  columnCss,
  textAreaContainerCss,
} from "./CauseAndEffectOrganizer.styles";

const MIN_PAIRS = 4;

const CauseAndEffectOrganizer = ({
  formData,
  onChange,
  title = "Cause and Effect Analysis",
}: TemplateProps<CauseAndEffectFormData>) => {
  const data = formData as CauseAndEffectFormData;

  const uniqueId = useRef(v4()).current;

  const articleTitle = data?.article_title || data?.articleShortTitle || "";

  const basePairs = data?.causes_and_effects || [];
  // Ensure minimum of #{MIN_PAIRS} pairs
  const pairs =
    basePairs.length >= MIN_PAIRS
      ? basePairs
      : [
          ...basePairs,
          ...Array.from({ length: MIN_PAIRS - basePairs.length }, () => ({
            cause: "",
            effect: "",
            source_text: "",
          })),
        ];

  const handleChange = (update: Partial<CauseAndEffectFormData>) => {
    if (!onChange) return;
    onChange(update);
  };

  const handlePairChange = (
    index: number,
    key: "cause" | "effect",
    value: string,
  ) => {
    const updatedPairs = pairs.map((pair, i) =>
      i === index
        ? {
            ...pair,
            [key]: value,
          }
        : pair,
    );
    handleChange({ causes_and_effects: updatedPairs });
  };

  return (
    <div css={containerCss}>
      <ExportablesTemplateHeaderSection
        templateTitle={title}
        articleTitle={articleTitle}
      />

      <div css={headersRowCss}>
        <div id="cause-header" css={columnHeaderCss}>
          Causes
        </div>
        <div />
        <div id="effect-header" css={columnHeaderCss}>
          Effects
        </div>
      </div>

      <div css={pairsContainerCss}>
        {pairs.map((pair, index) => {
          const causeId = `cause-input-${index}-${uniqueId}`;
          const effectId = `effect-input-${index}-${uniqueId}`;
          return (
            <div key={index} css={pairRowCss}>
              <div css={columnCss}>
                <TemplateTextArea
                  value={pair.cause || ""}
                  maxLength={INPUT_MAX_LENGTH}
                  css={textAreaContainerCss}
                >
                  <TemplateTextArea.TextArea
                    id={causeId}
                    onChange={(e) =>
                      handlePairChange(index, "cause", e.target.value)
                    }
                    aria-label={`Cause ${index + 1}`}
                    data-qa-selector={`cause-${index}`}
                  />
                  <TemplateTextArea.CharacterCount />
                </TemplateTextArea>
              </div>
              <ThinArrowRight __cssFor={{ root: arrowSvgCss }} isDecorative />
              <div css={columnCss}>
                <TemplateTextArea
                  value={pair.effect || ""}
                  maxLength={INPUT_MAX_LENGTH}
                  css={textAreaContainerCss}
                >
                  <TemplateTextArea.TextArea
                    id={effectId}
                    onChange={(e) =>
                      handlePairChange(index, "effect", e.target.value)
                    }
                    aria-label={`Effect ${index + 1}`}
                    data-qa-selector={`effect-${index}`}
                  />
                  <TemplateTextArea.CharacterCount />
                </TemplateTextArea>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CauseAndEffectOrganizer;
